import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { CrudModeEnum } from '../../../../../../core/base/enum/crud-mode.enum';
import icWork from '@iconify/icons-ic/twotone-work';
import icPhone from '@iconify/icons-ic/twotone-phone';
import icPersonAdd from '@iconify/icons-ic/twotone-person-add';
import icCheck from '@iconify/icons-ic/twotone-check';
import icMail from '@iconify/icons-ic/twotone-mail';
import icAccessTime from '@iconify/icons-ic/twotone-access-time';
import icAdd from '@iconify/icons-ic/twotone-add';
import { BuildingModel } from '../../../../../../core/models/building.model';
import { BuildingService } from '../../building.service';
import { AddressUtils } from '../../../../../../core/utils/address-utils';
import { scaleIn400ms } from '../../../../../../../@vex/animations/scale-in.animation';
import { fadeInRight400ms } from '../../../../../../../@vex/animations/fade-in-right.animation';
import { stagger40ms } from '../../../../../../../@vex/animations/stagger.animation';
import { fadeInUp400ms } from '../../../../../../../@vex/animations/fade-in-up.animation';
import { scaleFadeIn400ms } from '../../../../../../../@vex/animations/scale-fade-in.animation';
import { BuildingCreateUpdateDeleteComponent } from '../../modals/building-create-update-delete/building-create-update-delete.component';
import { FeiColumn } from '../../../../../../core/base/interfaces/fei-column.interface';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { StageCreateUpdateDeleteComponent } from '../../modals/stage-create-update-delete/stage-create-update-delete.component';
import { StageModel } from '../../../../../../core/models/stage.model';
import { BuildingEstablishmentCreateUpdateDeleteComponent } from '../../modals/building-establishment-create-update-delete/building-establishment-create-update-delete.component';
import { EstablishmentModel } from '../../../../../../core/models/establishment.model';
import { ENUM_PERMISSIONS } from '../../../../../../core/enums/permission.enum';
import { AddressModel } from '../../../../../../core/models/address.model';
import { AddressService } from '../../../../../../core/services/address.service';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { BuildingAddressModalComponent } from '../building-address-modal/building-address-modal.component';
import { BuildingEnergiesCreateUpdateComponent } from '../../modals/building-energies-create-update/building-energies-create-update.component';
import { AuthService } from '../../../../../../core/services/auth.service';
import { BuildingMetrikStatusComponent } from './building-metrik-status/building-metrik-status.component';
import { convertToHistoriqueEvents, getCurrentAndNextStatus } from '../../../../../../core/utils/metrikStatus-utils';
import { HistoriqueEvent } from '../../../../../../core/base/components/history/history.component';
import { DependenciesService } from '../../../../../../core/services/dependencies.service';
import {
  EndServicePreparationData,
  EndServicePreparationChecklist,
} from '../../../../../../core/models/building.model';
import { InventoryDocumentRequiredDialogComponent } from '../../modals/inventory-document-required-dialog/inventory-document-required-dialog.component';
import { SnackbarService } from '../../../../../../core/services/snackbar.service';
import { DefaultSupervisionMetrikTypeEnum } from '../../../../../../core/models/default-supervision.model';

@Component({
  selector: 'vex-building-details-resume',
  templateUrl: './building-details-resume.component.html',
  styleUrls: ['./building-details-resume.component.scss'],
  animations: [scaleIn400ms, fadeInRight400ms, stagger40ms, fadeInUp400ms, scaleFadeIn400ms],
})
export class BuildingDetailsResumeComponent implements OnInit, OnDestroy {
  protected subscription = new Subscription();

  icWork = icWork;
  icPhone = icPhone;
  icPersonAdd = icPersonAdd;
  icCheck = icCheck;
  icMail = icMail;
  icAccessTime = icAccessTime;
  icAdd = icAdd;
  addressFilters: any = {};

  // loading= false;
  ENUM_PERMISSION = ENUM_PERMISSIONS;
  metrikTypeEnum = DefaultSupervisionMetrikTypeEnum;
  entity: BuildingModel;

  displayEstablishmentSurfaceWarning = false;
  displayFavoriteWarning = false;
  isMainEstablishmentPresent = false;

  // End service preparation checklist
  endServicePreparationData: EndServicePreparationData = null;
  checklist: EndServicePreparationChecklist = {
    hasInventoryDocument: false,
    energyPointsHandled: [],
    principalAddressesUpdated: [],
    finessDeclarations: [],
    allActionsCompleted: false,
    historicalItems: {
      energyPointsHandled: [],
      principalAddressesUpdated: [],
      finessDeclarations: [],
    },
  };
  showChecklist = false;
  private saveChecklistTimeout: any = null;
  isValidatingActions = false; // État de chargement pour le bouton "Actions réalisées"
  isLoadingChecklist = false; // État de chargement pour la checklist

  // Tracker pour détecter l'ajout de nouveaux éléments
  private previousItemsCount = {
    energyPoints: 0,
    establishments: 0,
    finess: 0,
  };

  // Getter pour gérer les alertes combinées des établissements
  get hasEstablishmentWarnings(): boolean {
    return this.displayEstablishmentSurfaceWarning || (this.displayFavoriteWarning && !this.isMainEstablishmentPresent);
  }

  /**
   * Détermine si le bouton "AJOUTER UNE DATE DE FIN DE SERVICE" doit être activé
   *
   * Logique:
   * - Par défaut (chargement en cours): DÉSACTIVÉ pour éviter les clics prématurés
   * - Si pas de document "État des lieux": ACTIVÉ (pas d'actions préalables requises)
   * - Si document existe: ACTIVÉ uniquement si toutes les actions sont complétées
   */
  get isAddEndServiceButtonEnabled(): boolean {
    // Les données doivent être chargées (protection contre les clics pendant le chargement)
    if (!this.endServicePreparationData) {
      return false;
    }

    // Si pas de document "État des lieux", le bouton est actif (pas d'actions préalables)
    if (!this.endServicePreparationData.hasInventoryDocument) {
      return true;
    }

    // Si le document existe, vérifier que toutes les actions préalables sont complétées
    return this.checklist?.allActionsCompleted === true;
  }

  get combinedEstablishmentWarningTooltip(): string {
    const warnings: string[] = [];

    if (this.displayEstablishmentSurfaceWarning) {
      warnings.push(
        `La surface des établissements saisie ne correspond pas à la surface totale saisie du bâtiment. (Surface totale : ${this.entity?.surface} m²)`,
      );
    }

    if (this.displayFavoriteWarning && !this.isMainEstablishmentPresent) {
      warnings.push(`Il y a plus de deux établissements. Assignez un établissement comme contact achats`);
    }

    // Si plusieurs alertes, format avec des puces
    if (warnings.length > 1) {
      return warnings.map(warning => `• ${warning}`).join('\n');
    }

    // Si une seule alerte, pas de puce
    return warnings[0] || '';
  }

  feisColumnsStage: Array<FeiColumn> = [
    { label: 'Libellé', column: 'label' },
    { label: 'Niveau', column: 'level' },
    {
      label: '',
      column: 'actions',
      canUpdate: this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_BUILDING),
      canDelete: this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_BUILDING),
    },
  ];
  displayedColumnsStage = [];
  dataSourceStage: MatTableDataSource<any>;

  feisColumnsEnergy: Array<FeiColumn> = [
    { label: 'PDL', column: 'energyIdId' },
    { label: 'Type', column: 'genericEnergyId.energyType', propertyDisplay: 'genericEnergyId.energyType' },
    { label: 'Information complémentaire', column: 'additionalInfo' },
    { label: 'Fournisseur', column: 'supplier', propertyDisplay: 'supplier.label' },
    {
      label: '',
      column: 'actions',
      canUpdate: this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_BUILDING),
      canDelete: this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_BUILDING),
    },
  ];
  displayedColumnsEnergy = [];
  dataSourceEnergy: MatTableDataSource<any>;
  feisColumnsEstablishment: Array<FeiColumn> = [
    {
      label: 'Contact achats',
      column: 'favorite',
      canFavorite: this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_BUILDING),
    },
    { label: 'Libellé', column: 'label' },
    { label: "Surface occupé par l'établissement (m²)", column: 'surface' },
    { label: 'Nombre de lit permanent', column: 'nbOfPermanentBed' },
    {
      label: '',
      column: 'actions',
      canUpdate: this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_BUILDING),
      canDelete: this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_BUILDING),
      canFavorite: this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_BUILDING),
    },
  ];

  // feisColumnsEstablishmentNoFavorite: Array<FeiColumn> = [
  //     {label: 'Libellé', column: 'label'},
  //     {label: 'Surface occupé par le bâtiment (m²)', column: 'surface'},
  //     {label: 'Nombre de lit permanent', column: 'nbOfPermanentBed'},
  //     {label: '', column: 'actions', canUpdate: true, canFavorite: false}
  // ];
  //
  // feisColumnsEstablishmentFavorite: Array<FeiColumn> = [
  //     {label: 'Contact achats', column: 'favorite', canFavorite: true},
  //     {label: 'Libellé', column: 'label'},
  //     {label: 'Surface occupé par le bâtiment (m²)', column: 'surface'},
  //     {label: 'Nombre de lit permanent', column: 'nbOfPermanentBed'},
  //     {label: '', column: 'actions', canUpdate: true, canFavorite: true}
  // ];

  displayedColumnsEstablishment = [];
  dataSourceEstablishment: MatTableDataSource<any>;
  feisColumnsActivity: Array<FeiColumn> = [
    { label: 'Activité', column: 'activity', propertyDisplay: 'activity.label' },
    { label: 'Surface occupée (m²)', column: 'mc' },
  ];
  displayedColumnsActivity = this.feisColumnsActivity.map(x => x.column);
  dataSourceActivity: MatTableDataSource<any>;
  displayActivityWarning = false;
  firstInit = true;
  displayedColumnsAddress = ['actions'];
  countAddress = 0;
  dataSourceAddress = new MatTableDataSource<AddressModel>();
  sortFieldAddress: string;
  sortOrderAddress: string;
  pageIndexAddress: number;
  pageSizeAddress: number;
  feisColumnsAddress: Array<FeiColumn> = [
    { label: 'Principal', column: 'favorite', canFavorite: true },
    { label: 'Adresse', column: 'street', propertyDisplay: null },
    { label: "Complément d'adresse", column: 'additionalAddress', propertyDisplay: null },
    { label: 'Code postal', column: 'postalCode', propertyDisplay: null },
    { label: 'Ville', column: 'city', propertyDisplay: null },
    { label: 'Pays', column: 'country', propertyDisplay: null },
    {
      label: '',
      column: 'actions',
      canUpdate: this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_BUILDING),
      canDelete: this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_BUILDING),
    },
  ];
  displayEstablishmentWarning = true;
  clearSelectedEl = false;
  selectedEl = [];

  routerLinkEstablishment = '/management/establishment/:id';
  historiqueData: HistoriqueEvent[] = [];

  protected readonly ENUM_PERMISSIONS = ENUM_PERMISSIONS;

  constructor(
    public buildingService: BuildingService,
    private dialog: MatDialog,
    private router: Router,
    public authService: AuthService,
    private dependencyService: DependenciesService<BuildingModel>,
    private addressService: AddressService,
    private snackbarService: SnackbarService,
  ) {
    this.displayedColumnsStage = this.feisColumnsStage.map(x => x.column);
    this.displayedColumnsEnergy = this.feisColumnsEnergy.map(x => x.column);
    this.displayedColumnsActivity = this.feisColumnsActivity.map(x => x.column);
  }

  ngOnInit() {
    this.initData();
  }

  initData() {
    // Ajouter la subscription au Subscription pour éviter les fuites mémoire
    this.subscription.add(
      this.buildingService.entity$.subscribe((entity: BuildingModel) => {
        this.entity = entity;

        this.dataSourceStage = new MatTableDataSource<any>(entity?.stages);
        this.dataSourceEnergy = new MatTableDataSource<any>(entity?.energies || []);
        this.dataSourceEstablishment = new MatTableDataSource<any>(entity?.establishments);
        this.dataSourceActivity = new MatTableDataSource<any>(entity?.activities);
        this.loadBuildingAddresses(entity._id);

        this.displayActivityWarning = entity?.activities?.reduce((p, c) => p + c.mc, 0) !== entity?.surface;
        this.displayEstablishmentSurfaceWarning =
          entity?.establishments.reduce((p, c) => p + c.surface, 0) !== entity?.surface;
        this.displayEstablishmentWarning = !(entity?.establishments.length > 0);
        this.displayFavoriteWarning = entity?.establishments.length > 1;

        this.isMainEstablishmentPresent = entity.establishments.some(e => e.isMain === true);

        this.displayedColumnsEstablishment = this.feisColumnsEstablishment.map(x => x.column);

        // if (this.displayFavoriteWarning) {
        //     this.feisColumnsEstablishment = this.feisColumnsEstablishmentFavorite;
        //     this.displayedColumnsEstablishment = this.feisColumnsEstablishmentFavorite.map(x => x.column);
        // } else {
        //     this.feisColumnsEstablishment = this.feisColumnsEstablishmentNoFavorite;
        //     this.displayedColumnsEstablishment = this.feisColumnsEstablishmentNoFavorite.map(x => x.column);
        // }
        this.historiqueData = convertToHistoriqueEvents(getCurrentAndNextStatus(entity.metrikStatus));

        // TOUJOURS charger les données de checklist pour l'affichage
        // Le guard dans syncHistoricalItemsWithCurrentData() empêchera la modification des données figées
        this.isLoadingChecklist = true;
        this.loadEndServicePreparationData(!this.firstInit);
        this.firstInit = false;
      }),
    );
  }

  async updateItem() {
    this.dialog
      .open(BuildingCreateUpdateDeleteComponent, {
        data: {
          defaults: await this.buildingService.entity,
          mode: CrudModeEnum.Update,
        },
      })
      .afterClosed()
      .subscribe((supplier: BuildingModel) => {
        if (supplier) {
          // this.initData();
        }
      });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  async createStage() {
    this.dialog
      .open(StageCreateUpdateDeleteComponent, {
        data: {
          building: await this.buildingService.entity,
          mode: CrudModeEnum.Create,
        },
      })
      .afterClosed()
      .subscribe((stage: StageModel) => {
        if (stage) {
          this.initData();
        }
      });
  }

  async updateItemStage(element: any) {
    this.dialog
      .open(StageCreateUpdateDeleteComponent, {
        data: {
          building: await this.buildingService.entity,
          defaults: element,
          mode: CrudModeEnum.Update,
        },
      })
      .afterClosed()
      .subscribe((stage: StageModel) => {
        if (stage) {
          this.initData();
        }
      });
  }

  async deleteItemStage(element: any) {
    this.dialog
      .open(StageCreateUpdateDeleteComponent, {
        data: {
          building: await this.buildingService.entity,
          defaults: element,
          name: `${element.label}`,
          mode: CrudModeEnum.Delete,
        },
      })
      .afterClosed()
      .subscribe((stageModel: StageModel) => {
        if (stageModel) {
          this.initData();
        }
      });
  }

  async createEnergy() {
    this.dialog
      .open(BuildingEnergiesCreateUpdateComponent, {
        data: {
          building: await this.buildingService.entity,
          mode: CrudModeEnum.Create,
        },
      })
      .afterClosed()
      .subscribe(energy => {
        if (energy) {
          this.initData();
        }
      });
  }

  async updateItemEnergy(element: any) {
    this.dialog
      .open(BuildingEnergiesCreateUpdateComponent, {
        data: {
          building: await this.buildingService.entity,
          energy: element,
          mode: CrudModeEnum.Update,
        },
      })
      .afterClosed()
      .subscribe(energy => {
        if (energy) {
          this.initData();
        }
      });
  }

  async deleteItemEnergy(element: any) {
    this.dialog
      .open(BuildingEnergiesCreateUpdateComponent, {
        data: {
          building: await this.buildingService.entity,
          energy: element,
          name: `${element.energyIdId}`,
          mode: CrudModeEnum.Delete,
        },
      })
      .afterClosed()
      .subscribe(energy => {
        if (energy) {
          this.initData();
        }
      });
  }

  async createMetrikStatus() {
    const building = await this.buildingService.entity;
    this.dialog
      .open(BuildingMetrikStatusComponent, {
        data: {
          building,
          isForMetrikStatus: true,
          mode: CrudModeEnum.Create,
        },
        width: '620px',
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.initData();
        }
      });
  }

  async addItemEstablishment() {
    this.dialog
      .open(BuildingEstablishmentCreateUpdateDeleteComponent, {
        data: {
          building: await this.buildingService.entity,
          mode: CrudModeEnum.Create,
        },
      })
      .afterClosed()
      .subscribe((stage: EstablishmentModel) => {
        if (stage) {
          this.initData();
        }
      });
  }

  async updateItemEstablishment($event: any) {
    this.dialog
      .open(BuildingEstablishmentCreateUpdateDeleteComponent, {
        data: {
          building: await this.buildingService.entity,
          defaults: $event,
          mode: CrudModeEnum.Update,
        },
      })
      .afterClosed()
      .subscribe((establishment: EstablishmentModel) => {
        if (establishment) {
          this.initData();
        }
      });
  }

  async favoriteItemEstablishment(element: any) {
    this.buildingService.updateMainEstablishment(this.entity._id, element._id, element.isMain).subscribe(res => {
      this.initData();
    });
  }

  selectedRow(selectedData: any) {
    if (this.clearSelectedEl) {
      this.clearSelectedEl = false;
    }
    this.selectedEl = selectedData;
  }

  async deleteItemEstablishment(element: any) {
    this.dialog
      .open(BuildingEstablishmentCreateUpdateDeleteComponent, {
        data: {
          defaults: element,
          building: await this.buildingService.entity,
          mode: CrudModeEnum.Delete,
          name: `${element.label}`,
        },
      })
      .afterClosed()
      .subscribe((stage: EstablishmentModel) => {
        if (stage) {
          this.initData();
        }
      });
  }

  // Les activités sont désormais en lecture seule

  async createAddress() {
    const entity = await this.buildingService.entity;

    this.dialog
      .open(BuildingAddressModalComponent, {
        data: {
          buildingIds: [entity._id],
          defaults: {} as AddressModel,
          mode: CrudModeEnum.Create,
        },
        width: '600px',
        maxWidth: '90vw',
      })
      .afterClosed()
      .subscribe((address: AddressModel) => {
        if (address) {
          this.loadBuildingAddresses(entity._id);

          if (!this.entity.mainAddressId) {
            this.favoriteMainAddress(address);
          }
        }
      });
  }

  async updateAddress(address: AddressModel) {
    const entity = await this.buildingService.entity;

    this.dialog
      .open(BuildingAddressModalComponent, {
        data: {
          buildingIds: [entity._id],
          defaults: address,
          mode: CrudModeEnum.Update,
        },
        width: '600px',
        maxWidth: '90vw',
      })
      .afterClosed()
      .subscribe((updatedAddress: AddressModel) => {
        if (updatedAddress) {
          this.loadBuildingAddresses(entity._id);
        }
      });
  }

  async deleteAddress(address: AddressModel) {
    const entity = await this.buildingService.entity;

    this.dialog
      .open(BuildingAddressModalComponent, {
        data: {
          buildingIds: [entity._id],
          defaults: address,
          name: AddressUtils.formatInline(address) || address.label || address.street,
          mode: CrudModeEnum.Delete,
        },
        width: '600px',
        maxWidth: '90vw',
      })
      .afterClosed()
      .subscribe((deleted: boolean) => {
        if (deleted) {
          this.loadBuildingAddresses(entity._id);
        }
      });
  }

  async setEndServiceDate() {
    // Guard : empêcher de définir une date de fin deux fois
    if (this.hasDefinitiveMetrik(this.entity)) {
      this.snackbarService.danger('Ce bâtiment a déjà une date de fin de service définitive.');
      return;
    }

    // Recharger les données pour s'assurer d'avoir l'état le plus récent
    await new Promise<void>(resolve => {
      this.buildingService.getEndServicePreparationData(this.entity._id).subscribe(response => {
        // La réponse a une double imbrication à cause du décorateur @Response
        this.endServicePreparationData = response.data?.data || response.data;
        console.log('🔍 endServicePreparationData:', this.endServicePreparationData);
        console.log('🔍 hasInventoryDocument:', this.endServicePreparationData?.hasInventoryDocument);
        resolve();
      });
    });

    // Vérifier d'abord la présence du document d'inventaire
    if (!this.checkInventoryDocumentBeforeEndService()) {
      console.log('❌ checkInventoryDocumentBeforeEndService returned false');
      return; // Arrêter si le document n'existe pas
    }
    console.log('✅ checkInventoryDocumentBeforeEndService returned true');

    const building = await this.buildingService.entity;

    const dependencies = await this.dependencyService
      .setService(this.buildingService)
      .setModuleName('bâtiment')
      .setModuleNameWithArticle('un bâtiment')
      .setModuleNameTitleWithArticle('du bâtiment')
      .check(building);

    if (dependencies.hasDependencies) {
      await this.dependencyService
        .setService(this.buildingService)
        .setModuleName(building.label)
        .setModuleNameWithArticle('un bâtiment')
        .setModuleNameTitleWithArticle('du bâtiment')
        .setActionType('end-service')
        .setIsStepByStep(false)
        .checkDependencies(building, _ => {
          // Petit délai pour éviter conflit fermeture/ouverture de modales
          setTimeout(() => {
            this.openMetrikModal();
          }, 300);
        });
    } else {
      await this.openMetrikModal();
    }
  }

  private async openMetrikModal() {
    const building = await this.buildingService.entity;
    this.dialog
      .open(BuildingMetrikStatusComponent, {
        data: {
          building,
          mode: CrudModeEnum.Custom,
        },
        width: '620px',
      })
      .afterClosed()
      .subscribe((building: BuildingModel) => {
        if (building) {
          // Recharger explicitement les données depuis le backend pour garantir la synchronisation
          this.buildingService.findOneById(building._id).subscribe(() => {
            console.log('✅ Building reloaded after set-end-service');
            // initData() est déjà appelé automatiquement via la subscription à entity$
          });
        }
      });
  }

  async pageEventAddress(event: PageEvent) {
    console.log(event);
    this.pageIndexAddress = event.pageIndex + 1;
    this.pageSizeAddress = event.pageSize;
  }

  async sortEventAddress(event: any) {
    this.sortFieldAddress = event.active;
    this.sortOrderAddress = event.direction;
  }

  private loadBuildingAddresses(buildingId: string) {
    this.addressFilters.buildingIds = [buildingId];

    // Charger les adresses via le service
    this.buildingService.loadBuildingAddresses(buildingId);

    this.subscription // S'abonner aux changements d'adresses
      .add(
        this.buildingService.address$.subscribe(address => {
          this.dataSourceAddress = new MatTableDataSource<AddressModel>(address);
          this.countAddress = address.length;
        }),
      );
  }

  async favoriteMainAddress(address: AddressModel) {
    console.log('favoriteMainAddress called with address:', address);
    this.buildingService.updateMainAddress(this.entity._id, address._id, address.isMain).subscribe(res => {
      console.log('updateMainAddress response:', res);
      // Le service appelle déjà loadBuildingAddresses, pas besoin de le refaire ici
    });
  }

  /**
   * Vérifie si une metrik définitive existe dans l'historique des metriks
   * Une metrik définitive (isDefinitivelyOver) ne peut être créée qu'une seule fois et est irréversible
   */
  hasDefinitiveMetrik(building: BuildingModel): boolean {
    if (!building?.metrikStatus || building.metrikStatus.length === 0) {
      return false;
    }
    return building.metrikStatus.some(metrik => metrik.isDefinitivelyOver === true);
  }

  /**
   * Charge les données de préparation pour la checklist de fin de service
   */
  loadEndServicePreparationData(preserveChecklist: boolean = false) {
    if (!this.entity?._id) return;

    this.buildingService.getEndServicePreparationData(this.entity._id).subscribe(response => {
      // La réponse a une double imbrication à cause du décorateur @Response
      this.endServicePreparationData = response.data?.data || response.data;

      // Vérifier si le document "État des lieux de sortie" existe
      if (!this.endServicePreparationData?.hasInventoryDocument) {
        // Si le document n'existe pas, on masque la checklist
        this.showChecklist = false;
        this.isLoadingChecklist = false;
      } else {
        // Si le document existe, on affiche la checklist
        this.showChecklist = true;

        // Ne réinitialiser la checklist que si demandé (au chargement initial)
        if (!preserveChecklist) {
          // Initialiser la checklist avec les données existantes du bâtiment OU une checklist vide
          if (this.entity.endServicePreparationChecklist) {
            this.checklist = { ...this.entity.endServicePreparationChecklist };
            // S'assurer que historicalItems existe même si elle vient de la base de données
            if (!this.checklist.historicalItems) {
              this.checklist.historicalItems = {
                energyPointsHandled: [],
                principalAddressesUpdated: [],
                finessDeclarations: [],
              };
            }
          } else {
            // Initialiser une checklist vide
            this.checklist = {
              hasInventoryDocument: this.endServicePreparationData.hasInventoryDocument,
              energyPointsHandled: [],
              principalAddressesUpdated: [],
              finessDeclarations: [],
              allActionsCompleted: false,
              historicalItems: {
                energyPointsHandled: [],
                principalAddressesUpdated: [],
                finessDeclarations: [],
              },
            };
          }
        }

        // Synchroniser historicalItems avec les données actuelles pour affichage
        const hasHistoricalChanges = this.syncHistoricalItemsWithCurrentData();

        // Si des éléments ont été ajoutés à historicalItems, sauvegarder immédiatement
        if (hasHistoricalChanges) {
          this.saveChecklist();
        }

        // Détecter automatiquement l'ajout de nouveaux éléments
        this.checkForNewItemsAndResetIfNeeded();

        // Chargement terminé
        this.isLoadingChecklist = false;
      }
    });
  }

  /**
   * Détecte l'ajout de nouveaux éléments et réinitialise le statut de validation si nécessaire
   * Conserve les cases déjà cochées
   */
  private checkForNewItemsAndResetIfNeeded() {
    // Ne rien faire si le bâtiment a déjà une date de fin de service définitive
    if (this.hasDefinitiveMetrik(this.entity)) {
      console.log('🔒 Building already has definitive end date, skipping reset check');
      return;
    }

    // Calculer les nouveaux totaux
    const newCounts = {
      energyPoints: this.endServicePreparationData?.energyPoints?.length || 0,
      establishments: this.endServicePreparationData?.establishmentsWithMainAddress?.length || 0,
      finess: this.endServicePreparationData?.establishmentsWithFiness?.length || 0,
    };

    console.log('📊 Previous counts:', this.previousItemsCount);
    console.log('📊 New counts:', newCounts);

    // Vérifier si de nouveaux éléments ont été ajoutés
    const hasNewItems =
      newCounts.energyPoints > this.previousItemsCount.energyPoints ||
      newCounts.establishments > this.previousItemsCount.establishments ||
      newCounts.finess > this.previousItemsCount.finess;

    if (hasNewItems) {
      console.log('🆕 New items detected!');

      // Si les actions étaient déjà complétées, on réinitialise le statut
      if (this.checklist?.allActionsCompleted) {
        console.log('↩️ Resetting allActionsCompleted to false (checklist was previously completed)');

        // Réinitialiser le statut de validation
        this.checklist.allActionsCompleted = false;
        delete this.checklist.completedAt;

        // Sauvegarder automatiquement et recharger l'entité pour synchroniser l'UI
        this.buildingService.updateEndServiceChecklist(this.entity._id, this.checklist).subscribe(() => {
          console.log('✅ Checklist reset saved - reloading entity to sync UI');

          // Recharger l'entité depuis le backend pour garantir la synchronisation
          this.buildingService.findOneById(this.entity._id).subscribe(() => {
            console.log('✅ Entity reloaded - UI should now be in sync');
          });
        });
      } else {
        console.log('🔄 New items added to uncompleted checklist - no reset needed');
      }
    }

    // Mettre à jour les compteurs pour la prochaine détection
    this.updateItemsCount();
  }

  /**
   * Met à jour les compteurs d'éléments pour la détection de nouveaux ajouts
   */
  private updateItemsCount() {
    this.previousItemsCount = {
      energyPoints: this.endServicePreparationData?.energyPoints?.length || 0,
      establishments: this.endServicePreparationData?.establishmentsWithMainAddress?.length || 0,
      finess: this.endServicePreparationData?.establishmentsWithFiness?.length || 0,
    };
  }

  /**
   * Synchronise historicalItems avec les données actuelles du backend
   * Cela permet d'afficher les éléments dans la checklist même avant de cliquer sur "ACTIONS RÉALISÉES"
   */
  private syncHistoricalItemsWithCurrentData(): boolean {
    // Guard : ne pas synchroniser si le building a une date de fin de service définitive
    // Les données doivent rester figées au moment de la fin de service
    if (this.hasDefinitiveMetrik(this.entity)) {
      console.log('🔒 Building has definitive end date - skipping historical items sync');
      return false;
    }

    let hasChanges = false;

    // Initialiser historicalItems si nécessaire
    if (!this.checklist.historicalItems) {
      this.checklist.historicalItems = {
        energyPointsHandled: [],
        principalAddressesUpdated: [],
        finessDeclarations: [],
      };
    }

    // Créer un Set des IDs existants pour éviter les doublons
    const existingEnergyIds = new Set(this.checklist.historicalItems.energyPointsHandled.map(p => p.id));
    const existingEstablishmentIds = new Set(this.checklist.historicalItems.principalAddressesUpdated.map(e => e.id));
    const existingFinessIds = new Set(this.checklist.historicalItems.finessDeclarations.map(e => e.id));

    // Ajouter les nouveaux points d'énergie
    if (this.endServicePreparationData?.energyPoints) {
      this.endServicePreparationData.energyPoints.forEach((point: any) => {
        if (!existingEnergyIds.has(point._id)) {
          this.checklist.historicalItems.energyPointsHandled.push({
            id: point._id,
            label: point.label,
            establishment: point.establishment,
            energyType: point.energyType,
          });
          hasChanges = true;
        }
      });
    }

    // Ajouter les nouveaux établissements avec adresse principale
    if (this.endServicePreparationData?.establishmentsWithMainAddress) {
      this.endServicePreparationData.establishmentsWithMainAddress.forEach((est: any) => {
        if (!existingEstablishmentIds.has(est._id)) {
          this.checklist.historicalItems.principalAddressesUpdated.push({
            id: est._id,
            label: est.label,
            technicalIdentifier: est.technicalIdentifier,
          });
          hasChanges = true;
        }
      });
    }

    // Ajouter les nouveaux établissements avec FINESS
    if (this.endServicePreparationData?.establishmentsWithFiness) {
      this.endServicePreparationData.establishmentsWithFiness.forEach((est: any) => {
        if (!existingFinessIds.has(est._id)) {
          this.checklist.historicalItems.finessDeclarations.push({
            id: est._id,
            label: est.label,
            technicalIdentifier: est.technicalIdentifier,
            finess: est.finess,
          });
          hasChanges = true;
        }
      });
    }

    return hasChanges;
  }

  /**
   * Vérifier la présence du document d'inventaire avant de continuer
   * Si absent, affiche une modale d'avertissement
   */
  checkInventoryDocumentBeforeEndService(): boolean {
    if (!this.endServicePreparationData?.hasInventoryDocument) {
      // Afficher la modale d'avertissement
      this.dialog.open(InventoryDocumentRequiredDialogComponent, {
        width: '500px',
        disableClose: false,
      });
      return false;
    }
    return true;
  }

  /**
   * Gère le clic sur les checkboxes
   */
  onCheckboxClick(
    event: Event,
    category: 'energyPointsHandled' | 'principalAddressesUpdated' | 'finessDeclarations',
    itemId: string,
  ) {
    console.log('🖱️ Checkbox clicked!', category, itemId);

    // Empêcher la propagation pour éviter les conflits
    event.stopPropagation();

    this.toggleChecklistItem(category, itemId);
  }

  /**
   * Gère le toggle des cases à cocher
   */
  toggleChecklistItem(
    category: 'energyPointsHandled' | 'principalAddressesUpdated' | 'finessDeclarations',
    itemId: string,
  ) {
    // Guard : empêcher les modifications si fin de service définitive
    if (this.hasDefinitiveMetrik(this.entity)) {
      console.log('⚠️ Modification checklist impossible - fin de service définitive');
      return;
    }

    console.log('🔄 Toggle checkbox:', category, itemId);

    // S'assurer que le tableau existe
    if (!this.checklist[category]) {
      this.checklist[category] = [];
    }

    const index = this.checklist[category].indexOf(itemId);

    if (index === -1) {
      this.checklist[category].push(itemId);
      console.log(`✅ Added ${itemId} to ${category}`);
    } else {
      this.checklist[category].splice(index, 1);
      console.log(`❌ Removed ${itemId} from ${category}`);
    }

    // Réinitialiser le statut "actions complètes" quand une case est modifiée
    // Le bouton "ACTIONS RÉALISÉES" réapparaîtra et le bouton "AJOUTER DATE" se grisera
    this.checklist.allActionsCompleted = false;

    console.log('🔍 Current checklist state:', JSON.stringify(this.checklist, null, 2));
    console.log(
      `📊 Counts: energies=${this.checklist.energyPointsHandled?.length}, addresses=${this.checklist.principalAddressesUpdated?.length}, finess=${this.checklist.finessDeclarations?.length}`,
    );

    // Sauvegarder automatiquement
    this.saveChecklist();
  }

  /**
   * Vérifie si un élément est coché
   */
  isChecklistItemChecked(
    category: 'energyPointsHandled' | 'principalAddressesUpdated' | 'finessDeclarations',
    itemId: string,
  ): boolean {
    return this.checklist[category]?.includes(itemId) || false;
  }

  /**
   * Vérifie si toutes les actions sont complètes
   */
  areAllActionsCompleted(): boolean {
    if (!this.endServicePreparationData) return false;

    const allEnergyPointsChecked =
      this.endServicePreparationData.energyPoints.length === 0 ||
      this.endServicePreparationData.energyPoints.every(point =>
        this.checklist.energyPointsHandled.includes(point._id),
      );

    const allPrincipalAddressesChecked =
      this.endServicePreparationData.establishmentsWithMainAddress.length === 0 ||
      this.endServicePreparationData.establishmentsWithMainAddress.every(est =>
        this.checklist.principalAddressesUpdated.includes(est._id),
      );

    const allFinessDeclarationsChecked =
      this.endServicePreparationData.establishmentsWithFiness.length === 0 ||
      this.endServicePreparationData.establishmentsWithFiness.every(est =>
        this.checklist.finessDeclarations.includes(est._id),
      );

    return (
      this.checklist.hasInventoryDocument &&
      allEnergyPointsChecked &&
      allPrincipalAddressesChecked &&
      allFinessDeclarationsChecked
    );
  }

  /**
   * Sauvegarde la checklist
   */
  saveChecklist() {
    // Annuler le timeout précédent s'il existe
    if (this.saveChecklistTimeout) {
      clearTimeout(this.saveChecklistTimeout);
    }

    // Créer une copie profonde de la checklist pour capturer l'état actuel
    const checklistSnapshot = JSON.parse(JSON.stringify(this.checklist));

    // Attendre 500ms avant de sauvegarder pour permettre plusieurs changements
    this.saveChecklistTimeout = setTimeout(() => {
      console.log('💾 Saving checklist snapshot:', JSON.stringify(checklistSnapshot, null, 2));
      this.buildingService.updateEndServiceChecklist(this.entity._id, checklistSnapshot).subscribe(
        response => {
          console.log('✅ Checklist saved successfully:', response);

          // Mettre à jour UNIQUEMENT le champ checklist, pas toute l'entité
          // Pour éviter de perdre les données (establishments, energies, etc.)
          if (response.data?.endServicePreparationChecklist) {
            this.entity.endServicePreparationChecklist = response.data.endServicePreparationChecklist;
            console.log('🔄 Checklist updated:', this.entity.endServicePreparationChecklist);
          }
        },
        error => {
          console.error('❌ Error saving checklist:', error);
        },
      );
    }, 500);
  }

  /**
   * Actions réalisées - re-vérifie les dépendances puis valide si OK
   */
  async onActionsCompleted() {
    // Guard : empêcher la validation si fin de service définitive
    if (this.hasDefinitiveMetrik(this.entity)) {
      this.snackbarService.danger('Ce bâtiment a déjà une date de fin de service définitive.');
      return;
    }

    // Empêcher les clics multiples
    if (this.isValidatingActions) {
      return;
    }

    console.log('🔘 Re-checking dependencies before validation...');

    // Activer le spinner et désactiver le bouton
    this.isValidatingActions = true;

    // Étape 1 : Re-vérifier les dépendances
    this.buildingService.recheckEndServiceDependencies(this.entity._id).subscribe({
      next: recheckResponse => {
        console.log('📥 Recheck response:', recheckResponse);

        // La réponse a une double imbrication à cause du décorateur @Response
        const recheckResult = recheckResponse.data?.data || recheckResponse.data;
        console.log('📦 Recheck result:', recheckResult);

        const {
          newDependencies,
          stillPresentDependencies,
          disappearedDependencies,
          returningDependencies,
          hasChanges,
        } = recheckResult;

        // Initialiser historicalItems si nécessaire
        if (!this.checklist.historicalItems) {
          this.checklist.historicalItems = {
            energyPointsHandled: [],
            principalAddressesUpdated: [],
            finessDeclarations: [],
          };
        }

        // Mettre à jour hasInventoryDocument depuis les données actuelles
        console.log('🔍 Current hasInventoryDocument in checklist:', this.checklist.hasInventoryDocument);
        console.log('🔍 New hasInventoryDocument from currentData:', recheckResult.currentData?.hasInventoryDocument);

        if (recheckResult.currentData?.hasInventoryDocument !== undefined) {
          this.checklist.hasInventoryDocument = recheckResult.currentData.hasInventoryDocument;
          console.log('📋 Updated hasInventoryDocument to:', this.checklist.hasInventoryDocument);
        } else {
          console.warn('⚠️ hasInventoryDocument not found in currentData!');
        }

        // Ajouter les nouvelles dépendances détectées à l'historique
        if (newDependencies.energyPoints.length > 0) {
          newDependencies.energyPoints.forEach((point: any) => {
            this.checklist.historicalItems.energyPointsHandled.push({
              id: point._id,
              label: point.label,
              establishment: point.establishment,
              energyType: point.energyType,
            });
          });
        }

        if (newDependencies.establishmentsWithMainAddress.length > 0) {
          newDependencies.establishmentsWithMainAddress.forEach((est: any) => {
            this.checklist.historicalItems.principalAddressesUpdated.push({
              id: est._id,
              label: est.label,
              technicalIdentifier: est.technicalIdentifier,
            });
          });
        }

        if (newDependencies.establishmentsWithFiness.length > 0) {
          newDependencies.establishmentsWithFiness.forEach((est: any) => {
            this.checklist.historicalItems.finessDeclarations.push({
              id: est._id,
              label: est.label,
              technicalIdentifier: est.technicalIdentifier,
              finess: est.finess,
            });
          });
        }

        let hasBlockingIssues = false;
        let snackbarMessage = '';

        // Les dépendances qui reviennent restent dans l'historique mais sont décochées
        if (
          returningDependencies.energyPointsHandled.length > 0 ||
          returningDependencies.principalAddressesUpdated.length > 0 ||
          returningDependencies.finessDeclarations.length > 0
        ) {
          console.log('🔄 Historical dependencies returning, unchecking them');

          // Ajouter aux stillPresentDependencies pour les décocher
          stillPresentDependencies.energyPointsHandled.push(...returningDependencies.energyPointsHandled);
          stillPresentDependencies.principalAddressesUpdated.push(...returningDependencies.principalAddressesUpdated);
          stillPresentDependencies.finessDeclarations.push(...returningDependencies.finessDeclarations);
        }

        // 3. Vérifier les dépendances encore présentes dans le backend
        // Même si cochées, si elles existent toujours → erreur
        const uncheckedButStillPresentEnergy = recheckResult.currentData.energyPoints.filter(
          (point: any) => !this.checklist.energyPointsHandled.includes(point._id),
        );
        const uncheckedButStillPresentEstablishments = recheckResult.currentData.establishmentsWithMainAddress.filter(
          (est: any) => !this.checklist.principalAddressesUpdated.includes(est._id),
        );
        const uncheckedButStillPresentFiness = recheckResult.currentData.establishmentsWithFiness.filter(
          (est: any) => !this.checklist.finessDeclarations.includes(est._id),
        );

        // Récupérer les labels des dépendances cochées toujours présentes
        const stillPresentEnergyLabels: string[] = [];
        const stillPresentEstablishmentLabels: string[] = [];
        const stillPresentFinessLabels: string[] = [];

        stillPresentDependencies.energyPointsHandled.forEach(id => {
          const point = recheckResult.currentData.energyPoints.find((p: any) => p._id === id);
          if (point) {
            const label = `${point.energyType || 'Énergie'} - ${point.label || point._id}`;
            stillPresentEnergyLabels.push(label);
          }
        });

        stillPresentDependencies.principalAddressesUpdated.forEach(id => {
          const est = recheckResult.currentData.establishmentsWithMainAddress.find((e: any) => e._id === id);
          if (est) {
            const label = `Établissement - ${est.label || est.technicalIdentifier || est._id}`;
            stillPresentEstablishmentLabels.push(label);
          }
        });

        stillPresentDependencies.finessDeclarations.forEach(id => {
          const est = recheckResult.currentData.establishmentsWithFiness.find((e: any) => e._id === id);
          if (est) {
            const label = `Établissement FINESS - ${est.label || est.technicalIdentifier || est._id}`;
            stillPresentFinessLabels.push(label);
          }
        });

        // Récupérer les labels des dépendances non cochées
        const uncheckedEnergyLabels = uncheckedButStillPresentEnergy.map(
          (p: any) => `${p.energyType || 'Énergie'} - ${p.label || p._id}`,
        );
        const uncheckedEstablishmentLabels = uncheckedButStillPresentEstablishments.map(
          (e: any) => `Établissement - ${e.label || e.technicalIdentifier || e._id}`,
        );
        const uncheckedFinessLabels = uncheckedButStillPresentFiness.map(
          (e: any) => `Établissement FINESS - ${e.label || e.technicalIdentifier || e._id}`,
        );

        const totalUncheckedButPresent =
          uncheckedButStillPresentEnergy.length +
          uncheckedButStillPresentEstablishments.length +
          uncheckedButStillPresentFiness.length;

        // Gérer les dépendances cochées toujours présentes → DÉCOCHER + SNACKBAR ROUGE
        if (
          stillPresentDependencies.energyPointsHandled.length > 0 ||
          stillPresentDependencies.principalAddressesUpdated.length > 0 ||
          stillPresentDependencies.finessDeclarations.length > 0
        ) {
          hasBlockingIssues = true;
          const totalStillPresent =
            stillPresentDependencies.energyPointsHandled.length +
            stillPresentDependencies.principalAddressesUpdated.length +
            stillPresentDependencies.finessDeclarations.length;

          // Construire le message avec les labels
          const allStillPresentLabels = [
            ...stillPresentEnergyLabels,
            ...stillPresentEstablishmentLabels,
            ...stillPresentFinessLabels,
          ];

          snackbarMessage = `Dépendance(s) cochée(s) mais toujours présente(s) : ${allStillPresentLabels.join(', ')}`;

          // Décocher les dépendances toujours présentes
          this.checklist.energyPointsHandled = this.checklist.energyPointsHandled.filter(
            id => !stillPresentDependencies.energyPointsHandled.includes(id),
          );
          this.checklist.principalAddressesUpdated = this.checklist.principalAddressesUpdated.filter(
            id => !stillPresentDependencies.principalAddressesUpdated.includes(id),
          );
          this.checklist.finessDeclarations = this.checklist.finessDeclarations.filter(
            id => !stillPresentDependencies.finessDeclarations.includes(id),
          );

          console.log('⚠️ Still present dependencies detected, unchecked them');
        }

        // Vérifier les dépendances non cochées mais présentes
        if (totalUncheckedButPresent > 0) {
          hasBlockingIssues = true;

          // Construire le message avec les labels
          const allUncheckedLabels = [
            ...uncheckedEnergyLabels,
            ...uncheckedEstablishmentLabels,
            ...uncheckedFinessLabels,
          ];

          if (snackbarMessage) {
            snackbarMessage += ` - Dépendance(s) non cochée(s) : ${allUncheckedLabels.join(', ')}`;
          } else {
            snackbarMessage = `Dépendance(s) à traiter : ${allUncheckedLabels.join(', ')}`;
          }

          console.log('❌ Unchecked dependencies still present in backend');
        }

        // 4. Gérer les nouvelles dépendances → AJOUTER À LA CHECKLIST + SNACKBAR ROUGE
        if (
          newDependencies.energyPoints.length > 0 ||
          newDependencies.establishmentsWithMainAddress.length > 0 ||
          newDependencies.establishmentsWithFiness.length > 0
        ) {
          hasBlockingIssues = true;

          // Récupérer les labels des nouvelles dépendances
          const newEnergyLabels = newDependencies.energyPoints.map(
            (p: any) => `${p.energyType || 'Énergie'} - ${p.label || p._id}`,
          );
          const newEstablishmentLabels = newDependencies.establishmentsWithMainAddress.map(
            (e: any) => `Établissement - ${e.label || e.technicalIdentifier || e._id}`,
          );
          const newFinessLabels = newDependencies.establishmentsWithFiness.map(
            (e: any) => `Établissement FINESS - ${e.label || e.technicalIdentifier || e._id}`,
          );

          const allNewLabels = [...newEnergyLabels, ...newEstablishmentLabels, ...newFinessLabels];

          if (snackbarMessage) {
            snackbarMessage += ` - Nouvelle(s) dépendance(s) détectée(s) : ${allNewLabels.join(', ')}`;
          } else {
            snackbarMessage = `Nouvelle(s) dépendance(s) détectée(s) : ${allNewLabels.join(', ')}`;
          }

          console.log('🆕 New dependencies detected:', newDependencies);
        }

        // 5. Gérer les dépendances disparues → COCHER AUTOMATIQUEMENT (supprimée = traitée)
        let hasDisappearedDependencies = false;
        if (
          disappearedDependencies.energyPointsHandled.length > 0 ||
          disappearedDependencies.principalAddressesUpdated.length > 0 ||
          disappearedDependencies.finessDeclarations.length > 0
        ) {
          hasDisappearedDependencies = true;
          console.log('✅ Auto-checking disappeared dependencies (treated):', disappearedDependencies);

          // Cocher automatiquement les dépendances disparues
          disappearedDependencies.energyPointsHandled.forEach(id => {
            if (!this.checklist.energyPointsHandled.includes(id)) {
              this.checklist.energyPointsHandled.push(id);
            }
          });

          disappearedDependencies.principalAddressesUpdated.forEach(id => {
            if (!this.checklist.principalAddressesUpdated.includes(id)) {
              this.checklist.principalAddressesUpdated.push(id);
            }
          });

          disappearedDependencies.finessDeclarations.forEach(id => {
            if (!this.checklist.finessDeclarations.includes(id)) {
              this.checklist.finessDeclarations.push(id);
            }
          });
        }

        // Sauvegarder la checklist si des changements ont été faits (nouvelles dépendances ou dépendances disparues)
        if (hasBlockingIssues || hasDisappearedDependencies) {
          console.log('💾 Saving checklist with updates...');

          // Si des problèmes bloquants → remettre allActionsCompleted à false
          if (hasBlockingIssues) {
            console.log('⚠️ Blocking issues detected, resetting allActionsCompleted to false');
            this.checklist.allActionsCompleted = false;
          }

          this.buildingService.updateEndServiceChecklist(this.entity._id, this.checklist).subscribe(() => {
            console.log('✅ Checklist saved');

            // Si des problèmes bloquants → afficher snackbar rouge et ne pas valider
            if (hasBlockingIssues) {
              console.log('❌ Blocking issues detected, cannot validate');

              // Recharger les données pour avoir l'état à jour
              this.loadEndServicePreparationData(true);

              // Afficher snackbar rouge
              this.snackbarService.danger(snackbarMessage);

              this.isValidatingActions = false;
              return;
            }

            // 2. Si aucune dépendance → VALIDER AUTOMATIQUEMENT + SNACKBAR VERTE
            console.log('✅ No blocking dependencies, proceeding with validation');

            // Valider côté backend
            this.buildingService.validateEndServicePreparation(this.entity._id).subscribe({
              next: response => {
                console.log('📥 Validation response:', response);

                const validationResult = response.data?.data || response.data;
                console.log('📦 Validation result:', validationResult);

                if (validationResult.isValid) {
                  console.log('✅ Actions validated by backend');

                  // Recharger l'entité complète du bâtiment
                  this.buildingService.findOneById(this.entity._id).subscribe({
                    next: buildingResponse => {
                      console.log('🔄 Building entity reloaded');

                      // Mettre à jour le checklist local
                      if (this.entity?.endServicePreparationChecklist) {
                        this.checklist = { ...this.entity.endServicePreparationChecklist };
                        // S'assurer que historicalItems existe
                        if (!this.checklist.historicalItems) {
                          this.checklist.historicalItems = {
                            energyPointsHandled: [],
                            principalAddressesUpdated: [],
                            finessDeclarations: [],
                          };
                        }
                        console.log('✅ Local checklist updated:', this.checklist);
                      }

                      // Afficher snackbar verte
                      this.snackbarService.success('Toutes les actions sont validées !');

                      this.isValidatingActions = false;
                    },
                    error: error => {
                      console.error('❌ Error reloading building:', error);
                      this.isValidatingActions = false;
                    },
                  });
                } else {
                  // Validation échouée
                  console.log('❌ Validation failed:', validationResult.message);
                  this.snackbarService.danger(
                    validationResult.message || 'Les actions préalables ne sont pas toutes complétées',
                  );
                  this.isValidatingActions = false;
                }
              },
              error: error => {
                console.error('❌ Error validating actions:', error);
                this.isValidatingActions = false;
              },
            });
          });

          return;
        }

        // Si pas de changements dans la checklist mais pas de problèmes bloquants → valider directement
        console.log('✅ No changes needed, proceeding with validation');

        // Valider côté backend
        this.buildingService.validateEndServicePreparation(this.entity._id).subscribe({
          next: response => {
            console.log('📥 Validation response:', response);

            const validationResult = response.data?.data || response.data;
            console.log('📦 Validation result:', validationResult);

            if (validationResult.isValid) {
              console.log('✅ Actions validated by backend');

              // Recharger l'entité complète du bâtiment
              this.buildingService.findOneById(this.entity._id).subscribe({
                next: buildingResponse => {
                  console.log('🔄 Building entity reloaded');

                  // Mettre à jour le checklist local
                  if (this.entity?.endServicePreparationChecklist) {
                    this.checklist = { ...this.entity.endServicePreparationChecklist };
                    // S'assurer que historicalItems existe
                    if (!this.checklist.historicalItems) {
                      this.checklist.historicalItems = {
                        energyPointsHandled: [],
                        principalAddressesUpdated: [],
                        finessDeclarations: [],
                      };
                    }
                    console.log('✅ Local checklist updated:', this.checklist);
                  }

                  // Afficher snackbar verte
                  this.snackbarService.success('Toutes les actions sont validées !');

                  this.isValidatingActions = false;
                },
                error: error => {
                  console.error('❌ Error reloading building:', error);
                  this.isValidatingActions = false;
                },
              });
            } else {
              // Validation échouée
              console.log('❌ Validation failed:', validationResult.message);
              this.snackbarService.danger(
                validationResult.message || 'Les actions préalables ne sont pas toutes complétées',
              );
              this.isValidatingActions = false;
            }
          },
          error: error => {
            console.error('❌ Error validating actions:', error);
            this.isValidatingActions = false;
          },
        });
      },
      error: error => {
        console.error('❌ Error rechecking dependencies:', error);
        this.isValidatingActions = false;
      },
    });
  }

  /**
   * Récupère les types d'énergie uniques depuis l'historique
   */
  getEnergyTypes(): string[] {
    if (!this.checklist.historicalItems?.energyPointsHandled) return [];
    const types = [...new Set(this.checklist.historicalItems.energyPointsHandled.map(point => point.energyType))];
    return types.filter(type => type); // Filtrer les undefined
  }

  /**
   * Récupère les points d'énergie par type depuis l'historique
   */
  getEnergyPointsByType(energyType: string) {
    if (!this.checklist.historicalItems?.energyPointsHandled) return [];
    return this.checklist.historicalItems.energyPointsHandled
      .filter(point => point.energyType === energyType)
      .map(point => ({
        _id: point.id,
        label: point.label,
        establishment: point.establishment,
        energyType: point.energyType,
      }));
  }

  /**
   * Récupère tous les établissements avec adresse principale depuis l'historique
   */
  getAllEstablishmentsWithMainAddress() {
    if (!this.checklist.historicalItems?.principalAddressesUpdated) return [];
    return this.checklist.historicalItems.principalAddressesUpdated.map(est => ({
      _id: est.id,
      label: est.label,
      technicalIdentifier: est.technicalIdentifier,
    }));
  }

  /**
   * Récupère tous les établissements avec FINESS depuis l'historique
   */
  getAllEstablishmentsWithFiness() {
    if (!this.checklist.historicalItems?.finessDeclarations) return [];
    return this.checklist.historicalItems.finessDeclarations.map(est => ({
      _id: est.id,
      label: est.label,
      technicalIdentifier: est.technicalIdentifier,
      finess: est.finess,
    }));
  }

  /**
   * Scroll vers la section Énergies du bâtiment
   */
  scrollToEnergiesSection(event: Event) {
    console.log('🔗 Scroll to energies section clicked');
    event.preventDefault();
    event.stopPropagation();

    const energiesSection = document.getElementById('building-resume-energies-card');
    if (energiesSection) {
      console.log('✅ Scrolling to energies section');
      energiesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      console.error('❌ Energies section not found');
    }
  }

  /**
   * Obtient le routerLink pour un établissement
   */
  getEstablishmentRoute(establishmentId: string): string {
    return `/management/establishment/${establishmentId}`;
  }

  /**
   * Navigue vers un établissement avec contexte de retour
   */
  navigateToEstablishment(establishmentId: string, event: Event): void {
    event.preventDefault();
    this.router.navigate(['/management/establishment', establishmentId], {
      state: {
        returnTo: 'building',
        buildingId: this.entity._id,
        buildingLabel: this.entity.label,
      },
    });
  }

  /**
   * TrackBy functions pour optimiser les ngFor
   */
  trackById(index: number, item: any): string {
    return item._id || index;
  }

  trackByString(index: number, item: string): string {
    return item || index.toString();
  }

  /**
   * Vérifie si toutes les actions préalables requises sont cochées
   * Utilisé pour activer/désactiver le bouton "ACTIONS RÉALISÉES"
   */
  get areAllRequiredActionsChecked(): boolean {
    if (!this.endServicePreparationData || !this.checklist) {
      return false;
    }

    // Vérifier les énergies
    const requiredEnergyIds = this.endServicePreparationData.energyPoints?.map(p => p._id) || [];
    const checkedEnergyIds = this.checklist.energyPointsHandled || [];
    const allEnergiesChecked = requiredEnergyIds.every(id => checkedEnergyIds.includes(id));

    // Vérifier les établissements avec adresse principale
    const requiredEstablishmentIds =
      this.endServicePreparationData.establishmentsWithMainAddress?.map(e => e._id) || [];
    const checkedEstablishmentIds = this.checklist.principalAddressesUpdated || [];
    const allEstablishmentsChecked = requiredEstablishmentIds.every(id => checkedEstablishmentIds.includes(id));

    // Vérifier les établissements avec FINESS
    const requiredFinessIds = this.endServicePreparationData.establishmentsWithFiness?.map(e => e._id) || [];
    const checkedFinessIds = this.checklist.finessDeclarations || [];
    const allFinessChecked = requiredFinessIds.every(id => checkedFinessIds.includes(id));

    return allEnergiesChecked && allEstablishmentsChecked && allFinessChecked;
  }

  /**
   * Vérifie si un item est disparu (dans l'historique mais pas dans les données actuelles)
   * Ces items seront affichés en grisé
   */
  isItemDisappeared(itemId: string): boolean {
    if (!this.endServicePreparationData) return false;

    // Vérifier si l'item existe dans les données actuelles
    const existsInEnergyPoints = this.endServicePreparationData.energyPoints?.some(p => p._id === itemId) || false;
    const existsInEstablishments =
      this.endServicePreparationData.establishmentsWithMainAddress?.some(e => e._id === itemId) || false;
    const existsInFiness =
      this.endServicePreparationData.establishmentsWithFiness?.some(e => e._id === itemId) || false;

    return !existsInEnergyPoints && !existsInEstablishments && !existsInFiness;
  }
}
